import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * A mono value with a copy affordance.
 *
 * Every identifier on this surface — a SHA, a repo slug, a chat id, a file path
 * — is something a reviewer ends up pasting into a terminal or a ticket. Making
 * that one click rather than a careful double-click is the difference between a
 * correct SHA and a truncated one.
 */
export function CopyValue({
  value,
  display,
  className,
  title,
}: {
  value: string;
  display?: React.ReactNode;
  className?: string;
  title?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      // A denied clipboard permission is not worth an error state; the value is
      // on screen and selectable either way.
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={title ?? `Copy ${value}`}
      className={cn(
        'group inline-flex max-w-full items-baseline gap-1.5 rounded-sm font-mono text-xs text-ink-2 transition-colors hover:text-ink',
        className,
      )}
    >
      <span className="truncate">{display ?? value}</span>
      <span aria-hidden className={cn('shrink-0 text-tiny', copied ? 'text-ok' : 'text-ink-3 opacity-0 group-hover:opacity-100')}>
        {copied ? 'copied' : 'copy'}
      </span>
    </button>
  );
}
